import { useState } from 'react';
import { line, Color, Size, t } from '@bms2/shared';
import { Button } from '../../components/controls';
import { Icon } from '../../components/Icon';
import { patch, post, FILES_PATH } from './api';
import { RecordUploadModal } from './RecordUploadModal';
import { StageBlocked, StageCard, type StageCardRowView } from './StageCard';
import { ringClass } from './DealProgress';
import styles from './DealPage.module.css';

export const LEADS_PATH = '/leads';

export interface LeadCardRowAnswer {
  id: string | null;
  key: string;
  label: string;
  placeholder: string;
  checked: boolean;
  notes: string | null;
  derived: boolean;
  satisfied: boolean;
  /** The question's place on its card; a card not yet opened is answered through it (decision 0331). */
  sortOrder?: number;
  /** The answer is an uploaded file (decision 0332). */
  takesFile?: boolean;
  fileId?: string | null;
  fileName?: string | null;
}

export interface LeadCardAnswer {
  stage: string;
  cardId: string | null;
  counter: string;
  rows: LeadCardRowAnswer[];
  complete: boolean;
  problems: string[];
  current: boolean;
  /** The current card, every card behind it, and Interest while Awareness is current (decision 0331). */
  editable?: boolean;
  previousStage?: string | null;
}

const stateOf = (card: LeadCardAnswer, cards: readonly LeadCardAnswer[]): 'current' | 'done' | 'todo' => {
  if (card.current) return 'current';
  const at = cards.indexOf(card);
  const currentAt = cards.findIndex((c) => c.current);
  return currentAt < 0 || at < currentAt ? 'done' : 'todo';
};

/**
 * The Lead's three StageCards (piece 20; figure 82): Awareness and Interest
 * over the seeded question pairs, Negotiation over the NDA read off the
 * Company and two items answered on the Lead alone, each the one StageCard
 * construction. The current card, every card behind it and Interest beside
 * Awareness take input; the current card carries Back (decision 0331).
 * 🔨 A question completes only checked over a non-empty note, and the stage
 * ends on Complete stage alone. MSA signed is answered by its upload, through
 * the Files tile's own modal and tagged to the Lead (decision 0332).
 */
/** 🔒 `writes` is the page's `editsRecord`: a viewer without Deal write, the Lead's module, is drawn the cards with no tick, note or move (decision 1000; T365). */
export function LeadStageCards({ leadId, cards, writes = true, apiBaseUrl = '', onChanged, onError }: { leadId: string; cards: LeadCardAnswer[]; writes?: boolean; apiBaseUrl?: string; onChanged: () => void; onError: (message: string) => void }) {
  /** The file-answered question an upload is for, with the files dropped on it. */
  const [uploading, setUploading] = useState<{ stage: string; row: LeadCardRowAnswer; files: readonly File[] } | null>(null);
  const writeRow = async (stage: string, row: LeadCardRowAnswer, body: Record<string, unknown>) => {
    // A question on an opened card writes by its id; one on a card not yet opened by its stage and place, which opens the card.
    const path = row.id ? `${LEADS_PATH}/${leadId}/questions/${row.id}` : `${LEADS_PATH}/${leadId}/stages/${stage}/questions/${row.sortOrder ?? 0}`;
    const answer = await patch(path, body, apiBaseUrl);
    if (!answer.ok) return onError(answer.message);
    onChanged();
  };
  const complete = async () => {
    const answer = await post(`${LEADS_PATH}/${leadId}/complete-stage`, {}, apiBaseUrl);
    if (!answer.ok) return onError(answer.message);
    onChanged();
  };
  const back = async () => {
    const answer = await post(`${LEADS_PATH}/${leadId}/step-back`, {}, apiBaseUrl);
    if (!answer.ok) return onError(answer.message);
    onChanged();
  };
  return (
    <div data-lead-stage-cards={String(cards.length)} className={styles.leadCards}>
      {cards.map((card) => {
        const state = stateOf(card, cards);
        const editable = writes && (card.editable ?? card.current);
        const rows: StageCardRowView[] = card.rows.map((row) => ({
          key: row.id ?? row.key,
          label: row.label,
          placeholder: row.placeholder,
          checked: row.checked,
          notes: row.notes,
          applicable: true,
          derived: row.derived,
          derivedText: row.satisfied ? t('lead.card.satisfied') : t('lead.card.derived'),
          file: row.takesFile
            ? {
                fileId: row.fileId ?? null,
                fileName: row.fileName ?? null,
                href: row.fileId ? `${apiBaseUrl}${FILES_PATH}/${row.fileId}/stream` : null,
                onFiles: (files) => setUploading({ stage: card.stage, row, files }),
                onRemove: () => void writeRow(card.stage, row, { fileId: null }),
              }
            : null,
        }));
        const byKey = (key: string) => card.rows.find((row) => (row.id ?? row.key) === key);
        return (
          <StageCard
            key={card.stage}
            stage={card.stage}
            subtitle={!card.current && state === 'todo' && editable ? t('lead.card.ahead') : null}
            counter={card.counter}
            state={state}
            rows={rows}
            editable={editable}
            notesLabel={t('lead.card.notes')}
            onCheck={(key, checked) => { const row = byKey(key); if (row && !row.derived) void writeRow(card.stage, row, { checked }); }}
            onNotes={(key, notes) => { const row = byKey(key); if (row && !row.derived) void writeRow(card.stage, row, { notes }); }}
            onBack={writes && card.current && card.previousStage ? () => void back() : null}
            backLabel={card.previousStage ? line(t('lead.card.back'), card.previousStage) : null}
            data={{ 'data-stage-card-complete': card.complete ? 'true' : 'false' }}
            note={card.current && card.stage === 'Negotiation' ? (card.complete ? t('lead.card.negotiationDone') : t('lead.card.negotiationOpen')) : null}
            foot={writes && card.current && card.stage !== 'Negotiation' ? (
              <>
                {!card.complete ? <StageBlocked problems={card.problems} prefix={t('lead.card.blocked')} /> : null}
                <Button size={Size.Medium} color={Color.Navy} text={[line(t('lead.card.complete'), card.stage)]} actions={[() => void complete()]} disabled={!card.complete} />
              </>
            ) : null}
          />
        );
      })}
      <RecordUploadModal
        key={uploading ? `${uploading.stage}-${uploading.row.id ?? uploading.row.key}` : 'closed'}
        open={uploading !== null}
        recordType="Lead"
        recordId={leadId}
        apiBaseUrl={apiBaseUrl}
        initialFiles={uploading?.files ?? []}
        onUploaded={async (file) => {
          if (!uploading) return;
          const path = uploading.row.id ? `${LEADS_PATH}/${leadId}/questions/${uploading.row.id}` : `${LEADS_PATH}/${leadId}/stages/${uploading.stage}/questions/${uploading.row.sortOrder ?? 0}`;
          const answer = await patch(path, { fileId: file.id }, apiBaseUrl);
          if (!answer.ok) throw new Error(answer.message);
        }}
        onCancel={() => setUploading(null)}
        onSaved={onChanged}
      />
    </div>
  );
}

/** The Lead's progress panel: the three stages as the ladder draws them, with their counters. */
export function LeadProgress({ cards }: { cards: readonly LeadCardAnswer[] }) {
  return (
    <ol className={styles.ladder} data-lead-progress={String(cards.length)}>
      {cards.map((card) => {
        const state = stateOf(card, cards);
        const ring = state === 'done' ? 'done' : state === 'current' ? 'now' : 'todo';
        return (
          <li key={card.stage} className={styles.rung} data-lead-progress-stage={card.stage} data-lead-progress-state={state}>
            <span className={`${styles.ring} ${ringClass(ring)}`} aria-hidden="true">
              {ring === 'done' ? <Icon meaning="confirmed" size="inline" /> : null}
            </span>
            <span className={state === 'current' ? styles.rungLabelNow : styles.rungLabel}>{line(card.stage, `· ${card.counter}`)}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** The Lead's condensed rail: A/I/N. */
export function LeadRail({ cards }: { cards: readonly LeadCardAnswer[] }) {
  return (
    <ol className={styles.rail} aria-label={t('lead.progress.panel')}>
      {cards.map((card) => {
        const state = stateOf(card, cards);
        const ring = state === 'done' ? 'done' : state === 'current' ? 'now' : 'todo';
        return (
          <li key={card.stage} className={styles.railStep} data-rail-stage={card.stage} data-rail-state={state} title={card.stage}>
            <span className={`${styles.railRing} ${ringClass(ring)}`}>{ring === 'done' ? <Icon meaning="confirmed" size="inline" /> : card.stage.charAt(0)}</span>
          </li>
        );
      })}
    </ol>
  );
}
